import React, { useState, useMemo, useEffect } from "react";
import { Table, Dropdown, Button, Tag, ConfigProvider, Avatar } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { cn } from "../../utils/cn";
import { getInitials } from "../../utils/helper";

// Wrap ResizeObserver callbacks to avoid loop-limit errors (common with dynamic tables)
if (typeof window !== "undefined" && window.ResizeObserver && !window.__patchedResizeObserver) {
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class ResizeObserver extends OriginalResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
  window.__patchedResizeObserver = true;
}

const CommonTable = ({
  columns,
  data,
  showActions = true,
  actions = [],
  onRowClick,
  sort = { sortType: "", sortOrder: 1 },
  setSort = () => { },
}) => {
  const [openDropdown, setOpenDropdown] = useState(null);

  // Suppress only the benign ResizeObserver loop error locally for this table.
  useEffect(() => {
    const handleResizeObserverError = (e) => {
      const msg = e?.message || e?.error?.message || "";
      if (msg.includes("ResizeObserver loop")) {
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", handleResizeObserverError, true);
    return () => window.removeEventListener("error", handleResizeObserverError, true);
  }, []);

  // Memoize columns to prevent unnecessary recalculations on re-renders
  const tableColumns = useMemo(() => {
    let cols = columns.map((column) => {
      // Sorting: enable sorter when sortType is provided on the column
      const withSorting = column.sortType
        ? {
          // do not sort locally; just emit sort info
          sorter: () => 0,
          sortDirections: ["ascend", "descend", "ascend"], // allow clicking to clear/toggle
          sortOrder:
            sort?.sortType === column.sortType
              ? sort?.sortOrder === 1
                ? "ascend"
                : "descend"
              : null,
        }
        : {};

      // Avatar column
      if (column.isAvatar) {
        return {
          ...column,
          ...withSorting,
          render: (text, record, index) => {
            const initials = getInitials(text);
            const content = column.render ? column.render(text, record, index) : text;
            return (
              <div className="flex items-center gap-3">
                <Avatar
                  size={40}
                  className={cn("flex-shrink-0 font-semibold ", record.originalData?.photo ? "" : "!bg-primary")}
                  src={record.originalData?.photo ?? ""}
                >
                  {initials}
                </Avatar>
                <span className="font-medium text-gray-900">{content}</span>
              </div>
            );
          },
        };
      }

      // Badge column
      if (column.isBadge) {
        const badgeClasses = {
          default: "bg-gray-100 text-gray-800",
          success: "bg-green-100 text-green-800",
          error: "bg-red-100 text-red-800",
          warning: "bg-yellow-100 text-yellow-800",
          info: "bg-blue-100 text-blue-800",
          primary: "bg-indigo-100 text-indigo-800",
          danger: "bg-red-100 text-red-800",
        };

        return {
          ...column,
          ...withSorting,
          render: (text, record) => {
            const badgeKey = `${column.key}Badge`;
            const badgeConfig = record[badgeKey];

            let badgeText = text;
            let badgeType = column.badgeType || "default";

            if (badgeConfig) {
              badgeText = badgeConfig.text || text;
              badgeType = badgeConfig.color || badgeConfig.type || badgeType;
            }

            if (column.customRender) {
              badgeText = column.customRender(text, record);
            }

            const baseClasses =
              "inline-flex items-center justify-center rounded px-2 py-0.5 text-xs font-medium min-w-[70px]";

            return (
              <Tag
                className={cn(
                  baseClasses,
                  badgeClasses[badgeType] || badgeClasses.default,
                  column.className
                )}
                style={column.badgeStyle}
              >
                {badgeText}
              </Tag>
            );
          },
        };
      }

      return {
        ...column,
        ...withSorting,
      };
    });

    // Actions column
    if (showActions && actions.length > 0) {
      cols.push({
        title: "Actions",
        key: "actions",
        width: 50,
        align: "center",
        className: "actions-column",
        render: (_, record) => {
          const isOpen = openDropdown === record.id;

          const filteredActions = actions
            .map((action) => {
              if (action.condition && !action.condition(record)) return null;
              if (action.transform) return action.transform(record, action);
              return action;
            })
            .filter(Boolean);

          if (!filteredActions.length) return null;

          const items = filteredActions.map((action, index) => ({
            key: index,
            label: (
              <Button
                title={action.title ?? action.label}
                className={cn(
                  "text-sm font-medium h-9 my-1 flex justify-start hover:!border-primary hover:!text-primary !min-w-full",
                  action.className
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick(record);
                  setOpenDropdown(null); // close dropdown
                }}
              >
                {action.icon &&
                  React.cloneElement(action.icon, { className: "mr-2" })}
                {action.label}
              </Button>
            ),
            className: "!p-0",
          }));

          return (
            <Dropdown
              menu={{
                items,
                onClick: (e) => e.domEvent?.stopPropagation(),
                className:
                  "min-w-[150px] p-2 rounded-lg shadow-lg border border-gray-100",
              }}
              trigger={["click"]}
              placement="bottomRight"
              overlayClassName="action-dropdown"
              open={isOpen}
              onOpenChange={(flag) =>
                setOpenDropdown(flag ? record.id : null)
              }
            >
              <Button
                type="text"
                icon={
                  <MoreOutlined className="text-gray-500 hover:text-primary" />
                }
                className="flex items-center justify-center hover:bg-gray-100 rounded-full w-8 h-8"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpenDropdown(record.id);
                }}
              />
            </Dropdown>
          );
        },
      });
    }

    return cols;
  }, [columns, actions, openDropdown, getInitials, showActions, sort]);

  const handleChange = (_pagination, _filters, sorter) => {
    if (!sorter || !sorter.column?.sortType) return;

    // antd cycles: ascend -> descend -> (may be undefined when cleared). Always emit something.
    const isSameColumn = sort?.sortType === sorter.column.sortType;

    let nextOrder;
    if (sorter.order === "ascend") nextOrder = 1;
    else if (sorter.order === "descend") nextOrder = -1;
    else {
      // cleared; keep the column but flip for the next request so API gets a direction
      nextOrder = isSameColumn ? (sort?.sortOrder === 1 ? -1 : 1) : 1;
    }

    setSort({
      sortType: sorter.column.sortType,
      sortOrder: nextOrder,
    });
  };

  return (
    <div
      className="w-full overflow-x-auto rounded-lg shadow-sm "
    >
      <ConfigProvider
        theme={{
          token: {
            colorBgContainer: "#ffffff",
            colorBorder: "#f0f0f0",
            colorPrimary: "#1890ff",
            colorText: "rgba(0, 0, 0, 0.88)",
            colorTextSecondary: "rgba(0, 0, 0, 0.45)",
            borderRadius: 8,
          },
          components: {
            Table: {
              headerBg: "#f8f9fa",
              headerColor: "#495057",
              headerSplitColor: "#e9ecef",
              borderColor: "#f0f0f0",
              rowHoverBg: "#f8f9fa",
              rowSelectedBg: "#f1f3f5",
              headerBorderRadius: 8,
              cellPaddingBlock: 16,
              cellPaddingInline: 16,
            },
          },
        }}
      >
        <Table
          columns={tableColumns}
          dataSource={data}
          className="w-full [&_.ant-table]:rounded-lg [&_.ant-table-thead>tr>th]:bg-gray-50 [&_.ant-table-thead>tr>th]:text-gray-700 [&_.ant-table-thead>tr>th]:font-semibold [&_.ant-table-thead>tr>th]:uppercase [&_.ant-table-thead>tr>th]:text-xs [&_.ant-table-thead>tr>th]:tracking-wide [&_.ant-table-tbody>tr>td]:p-4 [&_.ant-table-tbody>tr>td]:border-b [&_.ant-table-tbody>tr>td]:border-gray-100 [&_.ant-table-tbody>tr:hover>td]:bg-gray-50"
          rowKey="id"
          pagination={false}
          scroll={{ x: "100%" }}
          bordered={false}
          size="middle"
          onChange={handleChange}
          rowClassName={() => (onRowClick ? "cursor-pointer" : "")}
          onRow={(record) => ({
            onClick: () => {
              if (onRowClick) onRowClick(record);
            },
          })}
        />
      </ConfigProvider>
    </div>
  );
};

export default CommonTable;
